import { type FC, useMemo } from "react"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"

import type { RecordType, RecordWTimestamp } from "types/dashboard.types"
import { valueFormatter } from "helpers/formatters/plots/plots"
import { parseSeasonalAmpacity, getAmpacityAt } from "helpers/utils/seasonalAmpacity"

interface PercentageBarCellProps {
  records: RecordType[]
  threshold?: number
  seasonalThreshold?: Record<string, number>
  locale: string
}

const PercentageBarCell: FC<PercentageBarCellProps> = ({
  records,
  threshold,
  seasonalThreshold,
  locale,
}) => {
  const theme = useTheme()

  const seasons = useMemo(
    () => parseSeasonalAmpacity(seasonalThreshold),
    [seasonalThreshold],
  )
  const hasSeasonal = seasons.length > 0
  const hasThreshold = hasSeasonal || threshold != null

  const total = records.length
  const matching = !hasThreshold
    ? 0
    : records.filter((r) => {
        if (typeof r._value !== "number") return false

        if (hasSeasonal) {
          const time = (r as RecordWTimestamp)._time
          if (time == null) return false
          const limit = getAmpacityAt(seasons, new Date(time))
          return limit != null && (r._value as number) < limit
        }

        return (r._value as number) < (threshold as number)
      }).length
  const percentage = !hasThreshold || total === 0 ? null : (matching / total) * 100

  return (
    <Stack direction="row" alignItems="center" gap={1} width="100%">
      <Typography fontWeight="700" fontSize="0.8rem" sx={{ flexShrink: 0 }} noWrap>
        {percentage == null
          ? "—"
          : valueFormatter({
              value: percentage,
              suffix: "%",
              decimalPlaces: 0,
              locale,
              formatUnits: false,
            })}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 3,
          bgcolor: theme.palette.neutral[400],
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${percentage ?? 0}%`,
            height: "100%",
            borderRadius: 3,
            bgcolor: theme.palette.primary.main,
          }}
        />
      </Box>
    </Stack>
  )
}

export default PercentageBarCell
